import { getCurrentSessionId } from '@/hooks/currentSessionRoute';

/**
 * How the current session was launched, for the `X-Heidi-Surface` and
 * `X-Ehr-Profile-Name` request headers.
 *
 * The backend cannot work either value out on its own: an Epic Haiku webview, a
 * SMART-on-FHIR embedded browser, and a plain browser all reach it as
 * `X-Client-Id: web`, and the launch profile only ever existed on the client.
 * So the surfaces that know declare both here.
 *
 * Scoped to the session the launch produced, not to the page that declared it.
 * A page-lifetime holder loses both values the moment the user lands on the
 * session, which is where almost every journey event is emitted. Comparing
 * against the current session instead keeps the attribution for the whole
 * launched session while still refusing to attribute a later standalone
 * session in the same tab to that launch.
 *
 * Observability only. The backend validates the surface against a known set and
 * uses neither value for identity or authorization, so client control is
 * acceptable. Resolves synchronously so request interceptors stay on the hot
 * path. Never throws.
 */

export type LaunchSurface = 'smart_on_fhir' | 'haiku';

interface LaunchAttribution {
  surface: LaunchSurface;
  profileName: string | null;
  /** The session this launch produced; null while the launch is still in flight. */
  sessionId: string | null;
}

let current: LaunchAttribution | null = null;

/**
 * True when the declared launch still describes what the user is looking at.
 * An unbound launch is in flight, and an unknown current session means the user
 * is off the session pages entirely, so both keep the attribution.
 */
function appliesToCurrentSession(): boolean {
  if (!current || current.sessionId === null) {
    return current !== null;
  }
  const sessionId = getCurrentSessionId();
  return sessionId === undefined || sessionId === current.sessionId;
}

/** Declares how this launch arrived. Call as soon as the surface is known. */
export function setLaunchAttribution(
  surface: LaunchSurface,
  profileName?: string | null
): void {
  current = {
    surface,
    profileName: profileName && profileName.length > 0 ? profileName : null,
    sessionId: null,
  };
}

/**
 * Binds the declared launch to the session it produced, after which the
 * attribution applies to that session rather than to every session in the tab.
 */
export function bindLaunchAttributionToSession(sessionId: string): void {
  if (current) {
    current = { ...current, sessionId };
  }
}

/** The launch surface, or null when this is not a launched session. */
export function getLaunchSurface(): LaunchSurface | null {
  return appliesToCurrentSession() && current ? current.surface : null;
}

/** The EHR launch profile, or null when unset or out of scope. */
export function getLaunchProfileName(): string | null {
  return appliesToCurrentSession() && current ? current.profileName : null;
}

/** @internal test-only helper. Resets the module-level value. */
export function __resetLaunchAttributionForTesting(): void {
  current = null;
}
